<template>
  <div id="admin-app">
    <template v-if="!isLoading && (isAuthenticated || $route.meta.allowAnonymous)">
      <template v-if="!$route.meta.hideLayout && isAuthenticated">
        <AdminHeader
          @mobile-menu-toggle="handleMobileMenuToggle"
          :mobile-menu-open="mobileMenuOpen"
        />
        <div class="admin-layout" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
          <AdminSidebar
            @toggle="handleSidebarToggle"
            @mobile-close="handleMobileMenuClose"
            :mobile-menu-open="mobileMenuOpen"
          />
          <main class="admin-main">
            <router-view></router-view>
          </main>
        </div>
      </template>
      <template v-else>
        <router-view></router-view>
      </template>
    </template>
    <template v-else-if="!isLoading">
      <Login />
    </template>
    <template v-else>
      <div class="loading-container">
        <div class="loading-spinner">Loading...</div>
      </div>
    </template>
    
    <!-- Global Notification Modal -->
    <NotificationModal />
  </div>
</template>

<script>
import AdminHeader from '@/components/AdminHeader.vue'
import AdminSidebar from '@/components/AdminSidebar.vue'
import Login from '@/views/Login.vue'
import NotificationModal from '@/components/NotificationModal.vue'
import { useAuth0 } from '@auth0/auth0-vue'
import { setAuth0Instance } from '@/services/api'

export default {
  name: 'AdminApp',
  components: {
    AdminHeader,
    AdminSidebar,
    Login,
    NotificationModal
  },
  data() {
    return {
      sidebarCollapsed: localStorage.getItem('sidebarCollapsed') === 'true',
      mobileMenuOpen: false
    }
  },
  setup() {
    const auth0 = useAuth0()
    const { isAuthenticated, isLoading } = auth0
    
    // Set the auth0 instance for the API service
    setAuth0Instance(auth0)
    
    return { isAuthenticated, isLoading }
  },
  methods: {
    handleSidebarToggle(collapsed) {
      this.sidebarCollapsed = collapsed
    },
    handleMobileMenuToggle(isOpen) {
      this.mobileMenuOpen = isOpen
    },
    handleMobileMenuClose() {
      this.mobileMenuOpen = false
    }
  }
}
</script>

<style>
:root {
  --primary: #10b981;
  --primary-dark: #0e9669;
  --primary-light: #d1fae5;
  --secondary: #1e293b;
  --text-dark: #1e293b;
  --text-medium: #4b5563;
  --text-light: #64748b;
  --bg-light: #f8fafc;
  --bg-white: #ffffff;
  --border-light: #e2e8f0;
  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  --radius-sm: 0.375rem;
  --radius-md: 0.75rem;
  --radius-lg: 1rem;
  --transition: all 0.3s ease;
  --danger: #ef4444;
  --danger-dark: #dc2626;
  --warning: #f59e0b;
  --warning-dark: #d97706;
  --success: #10b981;
  --success-dark: #059669;
  --info: #3b82f6;
  --info-dark: #2563eb;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  line-height: 1.6;
  color: var(--text-dark);
  background-color: var(--bg-light);
  overflow-x: hidden; /* Prevent horizontal scroll on mobile */
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: var(--bg-light);
}

.loading-spinner {
  font-size: 1.2rem;
  color: var(--text-medium);
}

#admin-app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden; /* Prevent horizontal scroll */
}

.admin-layout {
  display: flex;
  flex: 1;
}

.admin-main {
  flex: 1;
  padding: 2rem;
  background-color: var(--bg-white);
  margin: 1rem 1rem 1rem 0;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  display: flex;
  justify-content: center;
  overflow-x: hidden; /* Prevent horizontal scroll */
}

.admin-main > * {
  width: 100%;
  max-width: 1800px;
}

/* Handle sidebar collapsed state */
.admin-layout.sidebar-collapsed .admin-main {
  transition: margin-left 0.3s ease;
}

/* Mobile Responsive Layout */
@media (max-width: 768px) {
  .admin-layout {
    flex-direction: column;
  }

  .admin-main {
    padding: 1rem;
    margin: 0;
    border-radius: 0;
    min-height: calc(100vh - 60px); /* Account for header height */
  }

  .admin-card {
    padding: 1rem;
    margin-bottom: 0.75rem;
  }
}

/* Extra small screens */
@media (max-width: 480px) {
  .admin-main {
    padding: 0.75rem;
  }

  .admin-card {
    padding: 0.875rem;
  }
}

/* Admin-specific styling */
.admin-card {
  background: var(--bg-white);
  border-radius: var(--radius-md);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  margin-bottom: 1rem;
  border: 1px solid var(--border-light);
}

.admin-button {
  background-color: var(--primary);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: var(--transition);
}

.admin-button:hover {
  background-color: var(--primary-dark);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.admin-button.secondary {
  background-color: var(--text-medium);
}

.admin-button.secondary:hover {
  background-color: var(--text-dark);
}

.admin-button.danger {
  background-color: var(--danger);
}

.admin-button.danger:hover {
  background-color: var(--danger-dark);
}

.admin-button.warning {
  background-color: var(--warning);
}

.admin-button.warning:hover {
  background-color: var(--warning-dark);
}

.admin-button.info {
  background-color: var(--info);
}

.admin-button.info:hover {
  background-color: var(--info-dark);
}

.admin-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

.admin-table th,
.admin-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.admin-table th {
  background-color: #f9fafb;
  font-weight: 600;
  color: #374151;
}

.admin-table tbody tr:hover {
  background-color: #f9fafb;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
}

.status-badge.active {
  background-color: #dcfce7;
  color: #166534;
}

.status-badge.pending {
  background-color: #fef3c7;
  color: #92400e;
}

.status-badge.inactive {
  background-color: #fee2e2;
  color: #991b1b;
}
</style>